import { updateModuleCache, cacheAllModuleFiles } from '@/store/sync/cachingUtils';
import { performInMemorySearch } from '@/components/search/searchStore';
import { SYNC_STATES } from '@/store/sync/sync';
import employeeApi from './employeeApi';
import dataUtils from '@/common/dataUtils';
import moduleId from './moduleId';
import moduleIndex from './index';
import i18n from '@/plugins/i18n';
import localStorageManager from '../../services/browserStorageService.js';

const ROOT = { root: true };
function clone(obj) {
  return obj ? JSON.parse(JSON.stringify(obj)) : obj;
}
const state = {
  // Data
  personList: [],
  person: [],
  relation: [],
  metadata: [],
  section: [],
  tag: [],
  photo: [],
  metadataByLabel: {},
  photoByEmployeeId: {},
  personById: {},
  personItemById: {},
  personItemByEmployeeNr: {},

  // View State
  isLoaded: false, // true indicates, data has been loaded (at least one sync process)
};

const getters = {
  //
  /** ------------------------------------------------------------------------------------------------------------------
   * Module Implementation for unread badge counts by instance.
   * IMPLEMENT this getter to enayble unread counter badges
   * -------------------------------------------------------------------------------------------------------------------
   */
  getUnreadCountByModuleInstance: (state) => (instance) => {
    return 0;
  },
};

const mutations = {
  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION for UPDATING Module counters
   * ------------------------------------------------------------------------------------------------------------------ *
   */
  countModuleItems(state) {
    // Not implemented in this module
  },

  setData(state, data) {
    if (data) {
      let {
        personList,
        personItemById,
        personItemByEmployeeNr,
        person,
        metadata,
        relation,
        section,
        tag,
        photoByEmployeeId,
        personById,
        metadataByLabel,
        photo,
      } = data;
      state.personList = personList;
      state.personItemById = personItemById;
      state.personItemByEmployeeNr = personItemByEmployeeNr;
      state.person = person;
      state.metadata = metadata;
      state.metadataByLabel = metadataByLabel;
      state.relation = relation;
      state.section = section;
      state.tag = tag;
      state.photoByEmployeeId = photoByEmployeeId;
      state.personById = personById;
      state.photo = photo;

      state.isLoaded = true;
    }
  },
};

const actions = {
  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION for IN-MEMORY SEARCH
   * IMPLEMENT this action to enable module in-memory search
   * Call the action in search/searchStore (search for "Module in-memory searches" there)
   * Search all documents containing all of the space separated search words in state.filterCriteria
   * ------------------------------------------------------------------------------------------------------------------ *
   */
  searchModuleInMemoryContainingSearchTerm({ state }, { filterCriteria, options }) {
    return performInMemorySearch({
      moduleId,
      items: state.personList,
      titleSearchAttr: 'name',
      contentSearchAttr: null,
      searchCriteriaArray: filterCriteria,
      options,
    });
  },

  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION
   * Updates the browsers cache storage oevpad-api/modules/{moduleId} with the exact same data structure as received by the module sync api call!
   * Reason: This mutation MUST be called whenever data has changed, e.g. user_state data. It assures that the application cache is up-to-date
   *         when the user synchronized/updates in offline mode.
   * ------------------------------------------------------------------------------------------------------------------
   */
  async updateModuleApplicationCache({ state }) {},

  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION to synchronize files
   * Dispatched in store/sync !!!
   * Synchronize module files, that is storing files into browsers application cache by simply loading files from server.
   * The service-worker will intercept all requests and put files into cache if not yet there.
   * ------------------------------------------------------------------------------------------------------------------
   */
  async synchronizeFiles({ commit, dispatch, state }) {
    await cacheAllModuleFiles({ files: state.photo, moduleIndex, commit, dispatch });
  },

  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION to synchronize files
   * Dispatched in store/sync !!!
   * Synchronize module files, that is storing files into browsers application cache by simply loading files from server.
   * The service-worker will intercept all requests and put files into cache if not yet there.
   * ------------------------------------------------------------------------------------------------------------------
   */
  async synchronizeData({ commit, dispatch, rootState, rootGetters }) {
    try {
      const response = await employeeApi.getData();

      let { person, metadata, relation, section, tag } = response.data;
      let photo = person
        .map((item) => {
          let path = item && item.metadata && item.metadata.person && item.metadata.person.photo;
          let photoItem;
          // the fully qualified filename gets delivered here (eg https://origa-test.innotix.com/oevpad-cockpit/upload/employee/user-de6ab027-e3ba-414f-842c-3ba4425057d9.png)
          // this is unnecessary and does not work for offline capability. Therefore the filename needs to be extracted
          if (path) {
            let filenameArray = path.split('/');
            let filename = filenameArray.slice(-1);
            photoItem = { id: item.id, filename: filename[0] };
          }
          return photoItem;
        })
        .filter((photo) => !!photo);

      let photoByEmployeeId = dataUtils.buildIdMap(photo, 'id');
      let personById = dataUtils.buildIdMap(person, 'id');
      let relationBySourceUuid = dataUtils.buildIdMap(relation, 'sourceUuid');
      let relationByTargetUuid = dataUtils.buildIdMap(relation, 'targetUuid');

      let personList = person
        .filter((p) => p.metadata && p.metadata.person)
        .map((p) => {
          let { company, department, firstname, lastname, title } = p.metadata.person;
          //enhance item with information used when searching
          let moduleConfig = rootGetters['configs/getModuleConfig']({
            moduleType: moduleIndex.moduleType,
          });
          // TODO: is it ok?
          let subordinate = {};
          let boss = {};
          let representative = {};

          relation.forEach((r) => {
            if (r.sourceUuid === p.uuid) {
              if (r.targetType === 'supervisor') {
                boss[r.targetUuid] = {};
              }
            }
            if (r.targetUuid === p.uuid) {
              if (r.targetType === 'supervisor') {
                subordinate[r.sourceUuid] = {};
              }
            }
            if (r.sourceUuid === p.uuid) {
              if (r.targetType === 'representative') {
                representative[r.targetUuid] = {};
              }
            }
          });
          return {
            id: p.id,
            uuid: p.uuid,
            company,
            department,
            firstname,
            lastname,
            name: `${firstname} ${lastname}`,
            title,
            position: p.metadata && p.metadata.person && p.metadata.person.position,
            address: p.metadata.address,
            email: p.metadata.email,
            phone: p.metadata.phone,
            tags: p.metadata.tags,
            photoFileName: photoByEmployeeId[p.id] && photoByEmployeeId[p.id].filename,
            employeeNr: p.employeenr,
            subordinate,
            boss,
            representative,
            // add calculated data used mainly for displaying in live-search
            _moduleId: moduleId,
            _moduleTitle: moduleConfig.title,
            _displayLocation: '',
            _path: `/employee/${p.id}`,
          };
        });

      let personItemById = dataUtils.buildIdMap(personList, 'id');
      let personItemByUuid = dataUtils.buildIdMap(personList, 'uuid');
      let personItemByEmployeeNr = dataUtils.buildIdMap(personList, 'employeeNr');
      let metadataByLabel = dataUtils.buildIdMap(metadata, 'label');

      personList.forEach((person) => {
        for (const key in person.boss) {
          let boss = personItemByUuid[key];
          person.boss[key] = clone(boss);
        }
        for (const key in person.subordinate) {
          let sub = personItemByUuid[key];
          person.subordinate[key] = clone(sub);
        }
        for (const key in person.representative) {
          let rep = personItemByUuid[key];
          person.representative[key] = clone(rep);
        }
      });

      //console.debug('personList', personList);
      //console.debug('personItemById', personItemById);
      commit('setData', {
        personList,
        personItemById,
        personItemByEmployeeNr,
        metadataByLabel,
        person,
        metadata,
        relation,
        section,
        tag,
        photo,
        photoByEmployeeId,
        personById,
      });
    } catch (err) {
      console.error(err);
      commit('sync/setSyncState', { moduleId, syncState: SYNC_STATES.FAILURE, msg: err }, ROOT);
      throw err;
    }
  },

  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION
   * Dispatched in store/sync !!!
   * Gets dispatched after all sync processes have been completed.
   * This is the place to implement any post sync processes.
   * ------------------------------------------------------------------------------------------------------------------
   */
  async onSyncComplete({ commit, dispatch, rootState, rootGetters }) {
    localStorageManager.clearEmployeeAvatar();
  },

  async storeProfilePicture({ commit, dispatch, rootState, rootGetters }, { profilePictureBase64Image, employeeNr }) {
    let action = profilePictureBase64Image ? 'STORE_PROFILE_PICTURE' : 'STORE_PROFILE_PICTURE_DELETE';
    let subject = 'profilePictureChange';
    let moduleId = 'employee';
    let payload = { profilePictureBase64Image, employeeNr };
    let userState = {};
    let title = 'Profile picture change';
    let text = 'Profile picture change';
    let showAsUserInfo = false;
    let showInSendbox = false;
    let writeAuditLog = false;

    await dispatch(
      'sendbox/performSendboxItem2Action',
      { moduleId, subject, payload, userState, title, text, action, showAsUserInfo, showInSendbox, writeAuditLog },
      { root: true }
    );
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
